import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { FilenamePipe } from 'src/app/shared/pipes/filename.pipe';
import { UploadSources } from 'src/app/modules/private/file-manager/models';
import { FileManagerService } from 'src/app/modules/private/file-manager/services/file-manager.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { NotificationService } from '../../services/notification.service';
import { CommonModule } from '@angular/common';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { FileManagerCardComponent } from 'src/app/modules/private/file-manager/components/card/card.component';
import { FormsModule } from '@angular/forms';
import { DropFilesAreaComponent } from '../drop-files-area/drop-files-area.component';

@UntilDestroy()
@Component({
  selector: 'app-upload-files',
  templateUrl: './upload-files.component.html',
  styleUrl: './upload-files.component.scss',
  providers: [FilenamePipe],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzSelectModule,
    NzButtonModule,
    FileManagerCardComponent,
    DropFilesAreaComponent,
  ],
})
export class UploadFilesComponent implements OnInit, OnChanges {
  readonly fileManagerService = inject(FileManagerService);
  readonly message = inject(NzMessageService);
  readonly filenamePipe = inject(FilenamePipe);
  readonly notify = inject(NotificationService);
  @Input('previewMode') previewMode = false;
  @Input('showUploadButton') showUploadButton = false;
  @Input('clickable') clickable = true;
  @Input('source_name') source_name: any = UploadSources.other; // UploadSources
  @Input('source_id') source_id = undefined;
  @Input('uploadOnDrop') uploadOnDrop = false;
  @Input('cardActions') cardActions = null;
  @Input('borderless') borderless = false;
  @Output() countFiles = new EventEmitter<any>();
  @Output() isProcessing = new EventEmitter<boolean>();
  @Output() completed = new EventEmitter<any>();
  @Input() files = [];
  @Input() limit = Infinity;
  masterTags;
  masterCategory;
  processing = false;

  constructor() {
    this.fileManagerService.categories$.subscribe(categories => {
      this.masterCategory = categories.find(i => i.default)?.value;
    });
  }

  ngOnChanges(): void {
    this.countFiles.emit(this.files.length);
  }

  ngOnInit(): void {
    if (this.previewMode) {
      if (!this.cardActions) {
        this.cardActions = {
          tagging: {
            allow: false,
          },
          remove: {
            allow: false,
          },
        };
      }
    } else {
      this.cardActions = {
        tagging: {
          allow: true,
          visible: false,
        },
        remove: {
          allow: true,
        },
        update: {
          allow: this.uploadOnDrop,
        },
      };
    }
  }

  applyMasterFlags(): void {
    if (!this.processing) {
      this.files.forEach(file => {
        file.tag = this.masterTags;
        file.category = this.masterCategory;
      });
    }
  }

  filesDropped(files): void {
    if (!this.processing) {
      this.concatWithExisting(files);
      this.emitCount();

      if (this.uploadOnDrop) {
        this.upload();
      }
    }
  }

  concatWithExisting(files) {
    this.files = this.files.concat(files);
    this.countFiles.emit(this.files.length);
  }

  emitCount() {
    this.countFiles.emit(this.files.length);
  }

  onRemoveFile(file): void {
    this.files.splice(this.files.indexOf(file), 1);
    this.countFiles.emit(this.files.length);
    if (file.id && !this.previewMode) {
      this.fileManagerService.deleteFile(file.id).subscribe(() => {});
      this.uploadedFiles.splice(
        this.uploadedFiles.indexOf(
          this.uploadedFiles.find(i => i.id === file.id)
        ),
        1
      );
    }
  }

  onUpdateFile(file): void {
    file.status = 'loading';
    this.fileManagerService.updateFile(file, file.id).subscribe({
      next: res => {
        file.status = 'success';
        file.pending = false;
      },
      error: err => {
        file.status = 'error';
      },
    });
  }

  uploadedFiles = [];
  public upload() {
    this.uploadPromise().then(res => {
      this.completed.emit(this.uploadedFiles);
      this.uploadedFiles = [];
    });
  }

  public uploadPromise() {
    return new Promise((resolve, reject) => {
      if (!this.files.length) {
        reject('NO_FILES');
      } else {
        const valid = this.files.every(i => i.category);
        if (!valid) {
          this.message.create(
            'error',
            'Please select a category for the files'
          );
          reject('UNSPECIFIED_CATEGORY');
        } else {
          this.processing = true;
          this.isProcessing.emit(true);
          Promise.all([
            this.updateFiles(this.files.filter(i => i.id && i.pending)),
            this.uploadFiles(this.files.filter(i => !i.id)),
          ]).then(res => {
            this.processing = false;
            this.isProcessing.emit(false);
            if (res.every(i => i)) {
              resolve(this.uploadedFiles);
            } else {
              this.message.create(
                'error',
                'Something went wrong while uploading'
              );
            }
          });
        }
      }
    });
  }

  private updateFiles(files): Promise<boolean> {
    return new Promise(resolve => {
      if (!files.length) {
        resolve(true);
      } else {
        let count = 0;
        files.forEach(file => {
          file.status = 'loading';
          this.fileManagerService.updateFile(file, file.id).subscribe({
            next: res => {
              count++;
              file.status = 'success';
              file.pending = false;
              if (count === files.length) {
                this.notify.success(
                  'Upload Complete',
                  'Your files are now available and ready to use.'
                );
                resolve(true);
              }
            },
            error: err => {
              count++;
              file.status = 'error';
              if (count === files.length) {
                this.notify.error(
                  'Unexpected Error',
                  'An error occurred. Please contact support.'
                );
                resolve(false);
              }
            },
          });
        });
      }
    });
  }

  private uploadFiles(files): Promise<boolean> {
    const sourceName = this.source_name;
    const sourceId = this.source_id;

    return new Promise(resolve => {
      if (!files.length) {
        resolve(true);
      } else {
        let count = 0;
        let hasErrors = false;

        files.forEach(file => {
          file.status = 'loading';
          file.source_name = sourceName;
          file.source_id = sourceId;

          this.fileManagerService
            .uploadToNFS(this.fileManagerService.fileAsFormData(file), file)
            .then(
              res => {
                count++;
                file.id = res.id;
                file.status = 'success';
                file.pending = false;
                this.uploadedFiles.push(res);
                if (count === files.length) {
                  if (hasErrors) {
                    this.notify.error(
                      'Unexpected Error',
                      'An error occurred. Please contact support.'
                    );
                    resolve(false);
                  } else {
                    this.notify.success(
                      'Upload Complete',
                      'Your files are now available and ready to use.'
                    );
                    resolve(true);
                  }
                }
              },
              err => {
                hasErrors = true;
                count++;
                file.status = 'error';
                file.message = err.error || 'No specific message';
                if (count === files.length) {
                  if (hasErrors) {
                    this.notify.error(
                      'Unexpected Error',
                      'An error occurred. Please contact support.'
                    );
                    resolve(false);
                  } else {
                    this.notify.success(
                      'Upload Complete',
                      'Your files are now available and ready to use.'
                    );
                    resolve(true);
                  }
                }
              }
            );
        });
      }
    });
  }
}
